import {
	Fragment,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { createPortal } from 'react-dom';
import { getImageById } from '~/assets/collections';
import { playSound, Sound } from '~/assets/sounds';
import { ClothesImage } from '~/components/collections/ClothesImage';
import { CollectionImage } from '~/components/collections/CollectionImage';
import { Divider } from '~/components/ui/Divider';
import { useZoomFactor } from '~/hooks';
import { ItemType } from '~/shared';
import { TextLabel } from '../TextLabel';
import {
	getItemTypeTranslation,
	hintActions,
	InventoryCellProps,
} from './InventoryCell.model';
import {
	StyledContainer,
	StyledCountContainer,
	StyledCross,
	StyledDropdown,
	StyledHint,
	StyledHintItemImage,
	StyledHintTopContainer,
	StyledItemImageContainer,
	StyledPlaceholderContainer,
	StyledQuickAccessButton,
	StyledSelectItem,
	StyledSelectItemWrapper,
	StyledTag,
	StyledTagsContainer,
	StyledUsageLimit,
	StyledUsageLimitWrapper,
	StyledWrapper,
} from './InventoryCell.styled';

const InventoryCell = ({
	item,
	width = 1,
	height = 1,
	disabled,
	closed,
	activeHand,
	placeholderText,
	placeholderImage,
	quickAccessButton,
	isDragging,
	help,
	helpHover,
	hint,
	onDragStart,
	onDragOver,
	onDrop,
	onDragEnd,
	onAction,
	onMouseEnter,
	onMouseLeave,
	onClick,
	onContextMenu,
}: InventoryCellProps) => {
	const [hintVisible, setHintVisible] = useState(false);
	const [hintOpacityVisible, setHintOpacityVisible] = useState(false);
	const [hintPosition, setHintPosition] = useState({ top: 0, left: 0 });
	const containerRef = useRef<HTMLDivElement>(null);
	const ref = useRef<HTMLDivElement>(null);
	const openedAtRef = useRef<number>(0);
	const zoomFactor = useZoomFactor();
	const showPlaceholder = useMemo(
		() => !item && placeholderText && placeholderImage,
		[item, placeholderText, placeholderImage],
	);
	const [dragItemImage, setDragItemImage] = useState<string>('');

	const usageWidth = useMemo(() => {
		if (!item?.hasUsageLimit) return 0;
		if (item.usageLimit === undefined || item.usageLeft === undefined) return 0;
		return (item.usageLeft / item.usageLimit) * 100;
	}, [item]);

	const usageColor = useMemo(() => {
		if (item?.usageLeft === undefined || item?.usageLimit === undefined)
			return '#ffffff';
		if (item.usageLeft / item.usageLimit <= 0.3) return '#FF4343';
		return '#ffffff';
	}, [item]);

	const tags = useMemo(() => {
		if (!item) return [];
		const tags = [...(item.tags || [])];
		if (item.hasUsageLimit) {
			const usagePercent = Math.round(
				(item.usageLeft! / item.usageLimit!) * 100,
			);
			tags.push(`Осталось: ${usagePercent}%`);
		}
		return tags;
	}, [item]);

	const handleRightClick = useCallback(
		(e: React.MouseEvent) => {
			if (item && !hintVisible && e.type === 'contextmenu') {
				if (onContextMenu) {
					const result = onContextMenu(e);
					if (result === true) {
						e.preventDefault();
						return;
					}
				}
				e.preventDefault();
				if (containerRef.current) {
					const rect = containerRef.current.getBoundingClientRect();
					setHintPosition({
						top: rect.top / zoomFactor,
						left: rect.left / zoomFactor,
					});
				}
				openedAtRef.current = performance.now();
				setHintVisible(true);
				setTimeout(() => setHintOpacityVisible(true), 10);
			}
		},
		[hintVisible, item, zoomFactor, onContextMenu],
	);

	const closeHint = useCallback(() => {
		setHintOpacityVisible(false);
		setTimeout(() => setHintVisible(false), 200);
	}, []);

	const calculateHintPosition = useCallback(() => {
		if (!containerRef.current || !ref.current) return;

		const cellRect = containerRef.current.getBoundingClientRect();
		const hintRect = ref.current.getBoundingClientRect();

		const viewportWidth = window.innerWidth / zoomFactor;
		const viewportHeight = window.innerHeight / zoomFactor;

		const baseLeft = cellRect.left / zoomFactor;
		const baseTop = cellRect.top / zoomFactor;
		const cellBottom = cellRect.bottom / zoomFactor;
		const cellRight = cellRect.right / zoomFactor;

		let nextTop: number;
		let nextLeft: number;
		const hintHeight = hintRect.height / zoomFactor;
		const hintWidth = hintRect.width / zoomFactor;

		nextTop = baseTop;
		nextLeft = baseLeft;

		if (nextTop + hintHeight > viewportHeight) {
			nextTop = cellBottom - hintHeight;
		}
		nextTop = Math.max(10, Math.min(nextTop, viewportHeight - hintHeight));

		if (nextLeft + hintWidth > viewportWidth) {
			nextLeft = cellRight - hintWidth;
		}
		nextLeft = Math.max(10, Math.min(nextLeft, viewportWidth - hintWidth));

		setHintPosition({ top: nextTop, left: nextLeft });
	}, [zoomFactor]);

	useLayoutEffect(() => {
		if (!hintVisible) {
			return;
		}

		function handleClickOutside(event: Event) {
			if (performance.now() - openedAtRef.current < 120) {
				return;
			}
			if (ref.current && !ref.current.contains(event.target as Node)) {
				closeHint();
			}
		}

		function updateHintPosition() {
			calculateHintPosition();
		}

		// Моментальный пересчёт позиции сразу после маунта
		calculateHintPosition();
		document.addEventListener('click', handleClickOutside);
		document.addEventListener('contextmenu', handleClickOutside);
		window.addEventListener('scroll', updateHintPosition, true);
		window.addEventListener('resize', updateHintPosition, true);

		return () => {
			document.removeEventListener('click', handleClickOutside);
			document.removeEventListener('contextmenu', handleClickOutside);
			window.removeEventListener('scroll', updateHintPosition, true);
			window.removeEventListener('resize', updateHintPosition, true);
		};
	}, [ref, hintVisible, zoomFactor, closeHint, calculateHintPosition]);

	useEffect(() => {
		if (isDragging && hintVisible) {
			closeHint();
		}
	}, [isDragging, hintVisible, closeHint]);

	useEffect(() => {
		if (item) {
			getImageById(
				item.type === ItemType.CLOTHES || item.type === ItemType.BACKPACK
					? 'clothes'
					: 'items',
				item.itemId,
				'lg',
			)
				.then((image) => {
					setDragItemImage(image);
				})
				.catch((error) =>
					console.error(
						{ operation: 'inventory/loadDragImage', itemId: item.itemId },
						error,
					),
				);
		}
	}, [item]);

	const handleDragStart = (e: React.DragEvent) => {
		if (e.dataTransfer.setDragImage) {
			const dragImage = document.createElement('div');
			const baseSize = 80;
			const scaledSize = baseSize * zoomFactor;
			dragImage.style.width = `${scaledSize}px`;
			dragImage.style.height = `${scaledSize}px`;
			dragImage.style.position = 'absolute';
			dragImage.style.top = '-1000px';
			dragImage.style.left = '-1000px';
			dragImage.style.border = '1px solid rgba(255, 255, 255, 0.1)';
			dragImage.style.borderRadius = '10px';
			dragImage.style.backgroundColor = '#ffffff';
			dragImage.style.boxSizing = 'border-box';
			dragImage.style.pointerEvents = 'none';
			dragImage.style.userSelect = 'none';
			document.body.appendChild(dragImage);

			if (dragItemImage) {
				const img = document.createElement('img');
				img.src = dragItemImage;
				const size = (item?.type === ItemType.CLOTHES ? 60 : 80) * zoomFactor;
				img.style.width = `${size}px`;
				img.style.height = `${size}px`;
				img.style.position = 'absolute';
				img.style.top = '50%';
				img.style.left = '50%';
				img.style.transform = 'translate(-50%, -50%)';
				img.style.objectFit = 'contain';
				dragImage.appendChild(img);
			}

			e.dataTransfer.setDragImage(dragImage, scaledSize / 2, scaledSize / 2);

			setTimeout(() => {
				document.body.removeChild(dragImage);
			}, 0);
		}

		onDragStart?.(e);
	};

	return (
		<StyledWrapper
			$width={width}
			$height={height}
			ref={containerRef}
			onMouseEnter={() => {
				if (!disabled && !closed && !!item) {
					playSound(Sound.LinkCursor);
				}
			}}
		>
			<StyledContainer
				$width={width}
				$height={height}
				$active={!!item}
				$activeHand={activeHand}
				$disabled={disabled}
				$closed={closed}
				$hint={hintVisible}
				$help={help}
				$helpHover={helpHover}
				$dragHint={hint && !hintVisible}
				onContextMenu={handleRightClick}
				onClick={onClick}
				draggable={!disabled && !closed && !!item}
				onDragStart={handleDragStart}
				onDragOver={closed ? undefined : onDragOver}
				onDrop={closed ? undefined : onDrop}
				onDragEnd={onDragEnd}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
			>
				{closed && <StyledCross />}
				{item && (
					<StyledItemImageContainer
						$disabled={disabled}
						style={{ marginTop: height === 2 && !hintVisible ? 50 : 9 }}
					>
						{item.type === ItemType.CLOTHES ||
						item.type === ItemType.BACKPACK ? (
							<ClothesImage id={item.itemId} size={60} />
						) : (
							<CollectionImage collection="items" id={item.itemId} size="md" />
						)}
					</StyledItemImageContainer>
				)}
				{item && item.count && item.count > 1 ? (
					<StyledCountContainer>
						<TextLabel>{item.count?.toLocaleString('ru-RU')}</TextLabel>
					</StyledCountContainer>
				) : null}
				{showPlaceholder && (
					<StyledPlaceholderContainer>
						<img src={placeholderImage} />
						<TextLabel>{placeholderText}</TextLabel>
					</StyledPlaceholderContainer>
				)}
				{quickAccessButton && (
					<StyledQuickAccessButton $open={hintVisible}>
						<TextLabel>{quickAccessButton}</TextLabel>
					</StyledQuickAccessButton>
				)}
				{item?.hasUsageLimit && (
					<StyledUsageLimitWrapper>
						<StyledUsageLimit
							style={{ width: `${usageWidth}%`, backgroundColor: usageColor }}
						/>
					</StyledUsageLimitWrapper>
				)}
			</StyledContainer>
			{hintVisible &&
				createPortal(
					<StyledHint
						ref={ref}
						draggable={false}
						className={hintOpacityVisible ? 'visible' : ''}
						style={{
							position: 'fixed',
							top: `${hintPosition.top}px`,
							left: `${hintPosition.left}px`,
						}}
					>
						<StyledHintItemImage>
							{item &&
							(item.type === ItemType.CLOTHES ||
								item.type === ItemType.BACKPACK) ? (
								<ClothesImage id={item.itemId} size={60} />
							) : (
								item && (
									<CollectionImage
										collection="items"
										id={item.itemId}
										size="md"
									/>
								)
							)}
						</StyledHintItemImage>
						<StyledHintTopContainer>
							{item && (
								<TextLabel style={{ fontSize: 14, color: '#000000' }}>
									<span style={{ opacity: 0.5 }}>{item.name}</span>{' '}
									{item.subName && (
										<span style={{ fontWeight: 500 }}>{item.subName}</span>
									)}
								</TextLabel>
							)}
							<StyledTagsContainer>
								<StyledTag>
									{item && getItemTypeTranslation(item.type)}
								</StyledTag>
								{tags.map((tag) => (
									<StyledTag key={tag}>{tag}</StyledTag>
								))}
							</StyledTagsContainer>
						</StyledHintTopContainer>
						{item?.availableActions && item.availableActions.length > 0 && (
							<Fragment>
								<Divider
									$direction="H"
									$color="#000000"
									style={{ opacity: 0.1, margin: '0 20px' }}
								/>
								<StyledDropdown>
									{item.availableActions.map((action) => (
										<StyledSelectItemWrapper
											key={action}
											onClick={() => {
												onAction?.(action, item);
												closeHint();
												playSound(Sound.ClickButton);
											}}
										>
											<StyledSelectItem>
												<img src={hintActions[action].icon} />
												<TextLabel>{hintActions[action].name}</TextLabel>
											</StyledSelectItem>
										</StyledSelectItemWrapper>
									))}
								</StyledDropdown>
							</Fragment>
						)}
					</StyledHint>,
					document.body,
				)}
		</StyledWrapper>
	);
};

export { InventoryCell };
